// libraries
import { useMatch } from "@tanstack/react-location";
import { useMemo } from "react";
import {
  mIGRATION_createFeatureFlagsFromSource,
  FeatureFlags,
  featureIsEnabled,
  resolveFeatureFlags,
  TFeatureFlag,
} from "wombat-global/src/feature-flag";

// constants && types
import config from "../config";
import { TanUseMatch } from "../router/types";
import {
  IClientTarget,
  ISiteTarget,
  isClientTarget,
  isSiteTarget,
  isTenantTarget,
} from "wombat-global/src/typings";
import { getTargetFromParams } from "../router/utilities";

type UseFeatureFlagsResult = {
  flags: FeatureFlags;
  isLoading: boolean;
  hasFlag: (flag: TFeatureFlag) => boolean;
};

type UseFeatureFlagsFilterResult = {
  flags: FeatureFlags;
  enabled: boolean;
  isLoading: boolean;
  hasFlag: (flag: TFeatureFlag) => boolean;
};

export function useFeatureFlags(): UseFeatureFlagsResult;
export function useFeatureFlags(options: {
  target?: IClientTarget | ISiteTarget;
}): UseFeatureFlagsResult;

export function useFeatureFlags(options?: {
  flagFilter: TFeatureFlag[];
}): UseFeatureFlagsFilterResult;

export function useFeatureFlags(options?: {
  target: IClientTarget | ISiteTarget;
  flagFilter: TFeatureFlag[];
}): UseFeatureFlagsFilterResult;

export function useFeatureFlags(options?: {
  target?: IClientTarget | ISiteTarget;
  flagFilter?: TFeatureFlag[];
}): UseFeatureFlagsResult | UseFeatureFlagsFilterResult {
  const match = useMatch<TanUseMatch>();
  const { data, params, isLoading } = match || {};
  const { orgData, tenantData, siteData, divisionData } = data || {};

  const flags = useMemo(() => {
    const { target } = {
      target: getTargetFromParams(params || {}),
      ...(options || {}),
    };

    const dFeatureFlags = divisionData
      ? resolveFeatureFlags({
          availableFlags: config.availableFlags,
          defaultOnFlags: config.defaultOnFlags,
          targetFlags: mIGRATION_createFeatureFlagsFromSource(divisionData),
        })
      : config.defaultOnFlags;
    const tFeatureFlags = tenantData
      ? resolveFeatureFlags({
          availableFlags: config.availableFlags,
          defaultOnFlags: config.defaultOnFlags,
          targetFlags: mIGRATION_createFeatureFlagsFromSource(tenantData),
        })
      : config.defaultOnFlags;
    const oFeatureFlags = orgData
      ? resolveFeatureFlags({
          availableFlags: config.availableFlags,
          defaultOnFlags: config.defaultOnFlags,
          targetFlags: mIGRATION_createFeatureFlagsFromSource(orgData),
        })
      : config.defaultOnFlags;
    const sFeatureFlags = siteData
      ? resolveFeatureFlags({
          availableFlags: config.availableFlags,
          defaultOnFlags: config.defaultOnFlags,
          targetFlags: mIGRATION_createFeatureFlagsFromSource(siteData),
        })
      : config.defaultOnFlags;

    let _flags = config.defaultOnFlags;
    if (isTenantTarget(target) && target.divisionId) {
      _flags = dFeatureFlags;
    } else if (isTenantTarget(target)) {
      _flags = tFeatureFlags;
    } else if (isClientTarget(target) && target.divisionId) {
      _flags = dFeatureFlags;
    } else if (isClientTarget(target) && target.tenantId) {
      _flags = tFeatureFlags;
    } else if (isClientTarget(target) && target.orgId) {
      _flags = oFeatureFlags;
    } else if (isSiteTarget(target)) {
      _flags = sFeatureFlags;
    }

    return _flags;
  }, [divisionData, options, orgData, params, siteData, tenantData]);

  const loading = isLoading ?? false;
  const hasFlag = useMemo(
    () => (flag: TFeatureFlag) => featureIsEnabled(flags, flag),
    [flags],
  );
  const flagFilter = options?.flagFilter || [];

  if (flagFilter.length) {
    const enabled = flagFilter.every(hasFlag);
    return {
      flags,
      enabled,
      isLoading: loading,
      hasFlag,
    };
  }

  return {
    flags,
    isLoading: loading,
    hasFlag,
  };
}

export function useGlobalFeatureFlags(...flagFilter: TFeatureFlag[]): boolean {
  return flagFilter.every((flag) =>
    featureIsEnabled(config.defaultOnFlags, flag),
  );
}
